import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../shared/api/apiConfig';
import { useAuth } from '../../shared/auth/AuthContext';
import { getUserAvatarLabel } from '../../shared/auth/avatarLabel';
import './UserDetailPage.css';

function getProfileUser(profile) {
  if (!profile || typeof profile !== 'object') {
    return null;
  }

  if (profile.user && typeof profile.user === 'object') {
    return profile.user;
  }

  return profile;
}

function normalizeList(value, fallback = []) {
  if (Array.isArray(value)) {
    return value;
  }

  return Array.isArray(fallback) ? fallback : [];
}

function normalizeObject(value, fallback = {}) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  return fallback && typeof fallback === 'object' && !Array.isArray(fallback) ? fallback : {};
}

function normalizeProfilePayload(payload, previousProfile = null) {
  const sourceUser = getProfileUser(payload) ?? {};
  const previousUser = getProfileUser(previousProfile) ?? {};

  return {
    ...(payload && typeof payload === 'object' ? payload : {}),
    raw: payload,
    user: {
      ...previousUser,
      ...sourceUser,
      id: sourceUser.id ?? sourceUser.pk ?? previousUser.id ?? previousUser.pk ?? null,
      pk: sourceUser.pk ?? sourceUser.id ?? previousUser.pk ?? previousUser.id ?? null,
      name: sourceUser.name ?? previousUser.name ?? '',
      email: sourceUser.email ?? previousUser.email ?? '',
      username: sourceUser.username ?? previousUser.username ?? '',
      sub: sourceUser.sub ?? previousUser.sub ?? null,
      uid: sourceUser.uid ?? previousUser.uid ?? sourceUser.sub ?? previousUser.sub ?? null,
      avatar: sourceUser.avatar ?? previousUser.avatar ?? null,
      groups: normalizeList(sourceUser.groups, previousUser.groups),
      roles: normalizeList(sourceUser.roles, previousUser.roles),
      system_permissions: normalizeList(sourceUser.system_permissions, previousUser.system_permissions),
      settings: normalizeObject(sourceUser.settings, previousUser.settings),
    },
  };
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

function prettyPrint(value) {
  return JSON.stringify(value ?? {}, null, 2);
}

function buildProfileForm(profile) {
  const user = getProfileUser(profile);

  return {
    name: user?.name ?? '',
    email: user?.email ?? '',
  };
}

function formatEntityLabel(value) {
  if (typeof value === 'string') {
    return value;
  }

  return value?.name ?? value?.pk ?? '-';
}

function formatBoolean(value) {
  if (typeof value !== 'boolean') {
    return '-';
  }

  return value ? 'Yes' : 'No';
}

export default function UserDetailPage() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });
  const [saveState, setSaveState] = useState({ type: 'idle', message: '' });
  const [passwordState, setPasswordState] = useState({ type: 'idle', message: '' });

  async function loadProfile() {
    setLoadState({ type: 'pending', message: 'Loading your Authentik profile...' });

    try {
      const response = await fetch(apiUrl('/auth/user-detail'), { credentials: 'include' });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load your user details from Authentik.');
      }

      const normalizedProfile = normalizeProfilePayload(payload);

      setProfile(normalizedProfile);
      setProfileForm(buildProfileForm(normalizedProfile));
      setLoadState({ type: 'success', message: '' });
    } catch (error) {
      setLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load your user details from Authentik.',
      });
    }
  }

  useEffect(() => {
    loadProfile();
  }, []);

  const currentUser = getProfileUser(profile);
  const settings = currentUser?.settings ?? {};
  const canChangeName = settings?.default_user_change_name !== false;
  const canChangeEmail = settings?.default_user_change_email !== false;
  const avatarLabel = useMemo(() => getUserAvatarLabel(currentUser ?? user), [currentUser, user]);
  const isLoading = loadState.type === 'pending';
  const isSaving = saveState.type === 'pending';
  const isChangingPassword = passwordState.type === 'pending';

  function handleProfileFieldChange(event) {
    const { name, value } = event.target;
    setProfileForm((current) => ({ ...current, [name]: value }));
  }

  function handlePasswordFieldChange(event) {
    const { name, value } = event.target;
    setPasswordForm((current) => ({ ...current, [name]: value }));
  }

  async function handleProfileSubmit(event) {
    event.preventDefault();
    setSaveState({ type: 'pending', message: 'Saving profile changes...' });

    try {
      const response = await fetch(apiUrl('/auth/user-detail'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: profileForm.name,
          email: profileForm.email,
        }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to save your user details.');
      }

      const normalizedProfile = normalizeProfilePayload(payload, profile);

      setProfile(normalizedProfile);
      setProfileForm(buildProfileForm(normalizedProfile));
      setUser?.((current) => ({
        ...(current ?? {}),
        name: normalizedProfile.user?.name ?? current?.name ?? null,
        email: normalizedProfile.user?.email ?? current?.email ?? null,
        username: normalizedProfile.user?.username ?? current?.username ?? null,
      }));
      setSaveState({ type: 'success', message: 'Profile updated successfully.' });
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save your user details.',
      });
    }
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();

    if (!passwordForm.password) {
      setPasswordState({ type: 'error', message: 'Enter a new password first.' });
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      setPasswordState({ type: 'error', message: 'The password confirmation does not match.' });
      return;
    }

    setPasswordState({ type: 'pending', message: 'Updating password...' });

    try {
      const response = await fetch(apiUrl('/auth/user-detail/password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ password: passwordForm.password }),
      });

      let payload = null;
      if (response.status !== 204) {
        payload = await response.json().catch(() => null);
      }

      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to update your password.');
      }

      setPasswordForm({ password: '', confirmPassword: '' });
      setPasswordState({ type: 'success', message: 'Password updated successfully.' });
      await loadProfile();
    } catch (error) {
      setPasswordState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update your password.',
      });
    }
  }

  return (
    <main className="user-detail-page">
      <section className="user-detail-hero">
        <div className="user-detail-hero-copy">
          <p className="user-detail-eyebrow">Authentik account</p>
          <h1>User detail</h1>
          <p className="user-detail-subtitle">
            View the current Authentik profile, update the editable account fields,
            and change the password without leaving Checker.
          </p>
        </div>

        <div className="user-detail-summary-card">
          <div className="user-detail-avatar-shell">
            {currentUser?.avatar ? (
              <img src={currentUser.avatar} alt={currentUser.name ?? currentUser.username ?? 'User avatar'} className="user-detail-avatar-image" />
            ) : (
              <span className="user-detail-avatar-fallback">{avatarLabel || '??'}</span>
            )}
          </div>
          <strong>{currentUser?.name || currentUser?.username || 'Unknown user'}</strong>
          <span>{currentUser?.email || 'No email set'}</span>
          <div className="user-detail-summary-badges">
            <span>{currentUser?.groups?.length ?? 0} groups</span>
            <span>{currentUser?.roles?.length ?? 0} roles</span>
            <span>{currentUser?.system_permissions?.length ?? 0} permissions</span>
          </div>
        </div>
      </section>

      {loadState.type === 'error' && (
        <section className="user-detail-alert error" role="alert">
          {loadState.message}
        </section>
      )}

      <section className="user-detail-grid">
        <section className="user-detail-card">
          <div className="user-detail-card-head">
            <div>
              <h2>Editable profile</h2>
              <p>These values are saved back to Authentik.</p>
            </div>
          </div>

          <form className="user-detail-form" onSubmit={handleProfileSubmit}>
            <label className="user-detail-field">
              <span>Display name</span>
              <input
                name="name"
                value={profileForm.name}
                onChange={handleProfileFieldChange}
                disabled={isLoading || isSaving || !canChangeName}
                placeholder="Display name"
              />
            </label>

            <label className="user-detail-field">
              <span>Email</span>
              <input
                type="email"
                name="email"
                value={profileForm.email}
                onChange={handleProfileFieldChange}
                disabled={isLoading || isSaving || !canChangeEmail}
                placeholder="Email address"
              />
            </label>

            {(!canChangeName || !canChangeEmail) && (
              <p className="user-detail-note">
                Authentik has disabled one or more self-service profile fields for this account.
              </p>
            )}

            {saveState.type !== 'idle' && (
              <div className={`user-detail-inline-message ${saveState.type}`}>
                {saveState.message}
              </div>
            )}

            <div className="user-detail-actions">
              <button type="submit" disabled={isLoading || isSaving}>
                {isSaving ? 'Saving...' : 'Save profile'}
              </button>
            </div>
          </form>
        </section>

        <section className="user-detail-card">
          <div className="user-detail-card-head">
            <div>
              <h2>Change password</h2>
              <p>This updates the password stored in Authentik.</p>
            </div>
          </div>

          <form className="user-detail-form" onSubmit={handlePasswordSubmit}>
            <label className="user-detail-field">
              <span>New password</span>
              <input
                type="password"
                name="password"
                value={passwordForm.password}
                onChange={handlePasswordFieldChange}
                disabled={isChangingPassword}
                autoComplete="new-password"
              />
            </label>

            <label className="user-detail-field">
              <span>Confirm password</span>
              <input
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordFieldChange}
                disabled={isChangingPassword}
                autoComplete="new-password"
              />
            </label>

            {passwordState.type !== 'idle' && (
              <div className={`user-detail-inline-message ${passwordState.type}`}>
                {passwordState.message}
              </div>
            )}

            <div className="user-detail-actions">
              <button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? 'Updating...' : 'Update password'}
              </button>
            </div>
          </form>
        </section>

        <section className="user-detail-card">
          <div className="user-detail-card-head">
            <div>
              <h2>Detail</h2>
              <p>Read-only values returned by Authentik for this account.</p>
            </div>
          </div>

          <dl className="user-detail-definition-list">
            <div>
              <dt>ID</dt>
              <dd>{currentUser?.id ?? currentUser?.pk ?? '-'}</dd>
            </div>
            <div>
              <dt>Username</dt>
              <dd>{currentUser?.username ?? '-'}</dd>
            </div>
            <div>
              <dt>Subject (sub)</dt>
              <dd>{currentUser?.sub ?? currentUser?.uid ?? '-'}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{currentUser?.type ?? '-'}</dd>
            </div>
            <div>
              <dt>Active</dt>
              <dd>{formatBoolean(currentUser?.is_active)}</dd>
            </div>
            <div>
              <dt>Superuser</dt>
              <dd>{formatBoolean(currentUser?.is_superuser)}</dd>
            </div>
            <div>
              <dt>Groups</dt>
              <dd>
                <div className="user-detail-chip-list">
                  {(currentUser?.groups ?? []).length > 0 ? (
                    currentUser.groups.map((group, index) => (
                      <span key={`${formatEntityLabel(group)}-${index}`} className="user-detail-chip">
                        {formatEntityLabel(group)}
                      </span>
                    ))
                  ) : (
                    <span className="user-detail-empty-chip">No groups</span>
                  )}
                </div>
              </dd>
            </div>
          </dl>
        </section>

        <section className="user-detail-card user-detail-wide-card">
          <div className="user-detail-card-head">
            <div>
              <h2>Raw Authentik payload</h2>
              <p>Full payload for quick troubleshooting and validation.</p>
            </div>
          </div>

          <pre className="user-detail-pre">{prettyPrint(profile?.raw ?? profile)}</pre>
          {profile?.raw?.original && (
            <p className="user-detail-note">
              This session is impersonating another user. The <code>original</code> object is present in the Authentik response.
            </p>
          )}
          <p className="user-detail-note">
            Last password change: {formatDateTime(currentUser?.password_change_date)}
          </p>
          <p className="user-detail-note">
            Last updated: {formatDateTime(currentUser?.last_updated)}
          </p>
        </section>
      </section>
    </main>
  );
}
